/**
 * The techniques a planned graphic must use, and the shortlist a chooser picks them from.
 *
 * The shortlist is the whole library on purpose: narrowing it before the chooser sees it caps what
 * a video can reach.
 */
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { HTML_DESIGN_SKILL_META, PLAIN_TECHNIQUES } from './skillMeta';
import { findHtmlDesignSkill, getHtmlDesignSkills } from './skillRegistry';

export interface RequiredTechnique {
    skill: string;
    summary: string;
    for: string;
    /** The phase this technique belongs to, 1-based. Absent when it governs the whole graphic. */
    atPhase?: number;
    /** What write_html_asset reads to decide the technique was built, where the skill declares it. */
    checkLooksFor?: string;
}

export interface TechniqueAssignment {
    required: RequiredTechnique[];
    instruction: string;
}

export interface TechniqueCandidate {
    skill: string;
    summary: string;
    adventurous: boolean;
    /** Already assigned to an earlier scene in this video. Offered anyway, so a genuinely repeated
     *  kind of moment can look like the one before it. */
    usedAlready: boolean;
    requiredFromPlan?: string;
}

export interface ShortlistInput {
    sceneDurationMillis: number;
    /** What earlier scenes in this video were assigned. */
    alreadyAssigned?: ReadonlySet<string>;
}

/** What the assignment needs on top of the shortlist: the shape of the frame it is made for. */
export interface AssignmentInput extends ShortlistInput {
    /** Where the plan's phases begin, in seconds, in order. */
    phaseTimes: readonly number[];
    /** Whether the plan puts anything on screen that has to be drawn. */
    drawsObject: boolean;
    /** Skills whose instructions the model is still sent, so asking for them again adds nothing. */
    alreadyLoaded?: ReadonlySet<string>;
}

// Past two the assignment reads as a list rather than a decision.
const MAX_REQUIRED = 2;
const LONG_SCENE_MILLIS = 8000;

/** Held out of the shortlist so a slot is never spent on one; appended to every assignment. */
const CRAFT_STAPLES: ReadonlySet<string> = new Set([
    SKILL_NAMES.motionPolish,
    SKILL_NAMES.sustainedSceneReveal,
]);

function craftSkill(sceneDurationMillis: number): string {
    return sceneDurationMillis >= LONG_SCENE_MILLIS
        ? SKILL_NAMES.sustainedSceneReveal
        : SKILL_NAMES.motionPolish;
}

/** A phase time as it reads back to the agent — trailing zeroes are noise in a sentence. */
function seconds(value: number): string {
    return `${value.toFixed(1).replace(/\.0$/, '')}s`;
}

function summarize(name: string): string {
    return HTML_DESIGN_SKILL_META[name]?.indexSummary ?? name;
}

function requiredTechnique(skill: string, why: string, atPhase?: number): RequiredTechnique {
    const checkLooksFor = findHtmlDesignSkill(skill)?.checkLooksFor;
    return {
        skill,
        summary: summarize(skill),
        for: why,
        ...(atPhase === undefined ? {} : { atPhase }),
        ...(checkLooksFor ? { checkLooksFor } : {}),
    };
}

/** Plain entrances go last, so the bias toward the interesting ones is in the order as well as
 *  in the prompt. */
export function shortlist(input: ShortlistInput): TechniqueCandidate[] {
    const assigned = input.alreadyAssigned ?? new Set<string>();
    const candidates = getHtmlDesignSkills()
        .map(({ name }) => name)
        .filter((name) => !CRAFT_STAPLES.has(name))
        .map((name) => ({
            skill: name,
            summary: summarize(name),
            adventurous: !PLAIN_TECHNIQUES.has(name),
            usedAlready: assigned.has(name),
            ...(HTML_DESIGN_SKILL_META[name]?.requiredFromPlan
                ? { requiredFromPlan: HTML_DESIGN_SKILL_META[name].requiredFromPlan }
                : {}),
        }));
    return [
        ...candidates.filter(({ adventurous }) => adventurous),
        ...candidates.filter(({ adventurous }) => !adventurous),
    ];
}

export function assignmentFor(
    picks: { skill: string; why: string; atPhase?: number }[],
    input: AssignmentInput,
): TechniqueAssignment {
    const required: RequiredTechnique[] = picks
        .slice(0, MAX_REQUIRED)
        .map(({ skill, why, atPhase }) => requiredTechnique(skill, why, atPhase));
    required.push(
        requiredTechnique(craftSkill(input.sceneDurationMillis), 'craft, on every graphic'),
    );
    return { required, instruction: instructionFor(required, input) };
}

export function soleTechniqueAssignment(
    skill: string,
    input: AssignmentInput,
): TechniqueAssignment {
    const required: RequiredTechnique[] = [
        requiredTechnique(skill, 'the technique this graphic exists to show'),
    ];
    return { required, instruction: instructionFor(required, { ...input, drawsObject: false }) };
}

/** Not a fit judgement — nothing here reads the copy. It keeps a build moving. */
export function fallbackAssignment(input: AssignmentInput): TechniqueAssignment {
    const candidates = shortlist(input);
    const [first] = candidates.filter(({ usedAlready }) => !usedAlready);
    const picks = first
        ? [
              {
                  skill: first.skill,
                  why: 'assigned without a fit judgement — make it serve this frame',
              },
          ]
        : [];
    return assignmentFor(picks, input);
}

function namesOf(techniques: RequiredTechnique[]): string {
    return techniques.map(({ skill }) => `\`${skill}\``).join(', ');
}

/**
 * The opening of the assignment: which techniques to load, and which the conversation already
 * holds. A skill is sent in full every time it is loaded, so asking again for one already in the
 * context re-adds thousands of characters that every later call carries.
 */
function loadSentence(required: RequiredTechnique[], alreadyLoaded: ReadonlySet<string>): string {
    const plural = required.length > 1;
    const toLoad = required.filter(({ skill }) => !alreadyLoaded.has(skill));
    const held = required.filter(({ skill }) => alreadyLoaded.has(skill));
    const heldPronoun = held.length > 1 ? 'them' : 'it';
    const heldNote =
        `${namesOf(held)} ${held.length > 1 ? 'are' : 'is'} already loaded above — reread ` +
        `${heldPronoun} rather than loading ${heldPronoun} again`;
    if (toLoad.length === 0) {
        return `${heldNote} — and implement ${plural ? 'them' : 'it'} in this composition.`;
    }
    return (
        `Load ${namesOf(toLoad)} with ${TOOL_NAMES.loadSkill}` +
        (held.length > 0 ? ` (${heldNote})` : '') +
        ` and implement ${plural ? 'them' : 'it'} in this composition.`
    );
}

function instructionFor(required: RequiredTechnique[], input: AssignmentInput): string {
    const plural = required.length > 1;
    const alreadyLoaded = input.alreadyLoaded ?? new Set<string>();
    return [
        `${loadSentence(required, alreadyLoaded)} This is an assignment, not a menu: ${
            plural ? 'these are the techniques' : 'this is the technique'
        } this graphic uses. Read the implementation and build it — an entrance you wrote ` +
            `yourself is not a substitute, and a technique loaded but not applied is the same as ` +
            `never loading it. ${TOOL_NAMES.writeHtmlAsset} looks for each one in the composition ` +
            `you wrote — \`checkLooksFor\` says what it reads, where a technique has one — and fails ` +
            `the call when it is not there. Build the frame around ${
                plural ? 'them' : 'it'
            } — the assignment was made for the frame you described, so a frame that cannot host ` +
            `it is a frame you have designed away from the plan.`,
        placementNote(required, input.phaseTimes),
        drawingNote(
            required,
            input.drawsObject,
            alreadyLoaded.has(SKILL_NAMES.objectConstructionMethod),
        ),
    ]
        .filter(Boolean)
        .join('\n\n');
}

/**
 * Where a technique belongs, when the chooser placed it. A technique aimed at one phase is a thing
 * that happens at a moment; left unplaced it becomes a property of the whole graphic, and every
 * element gets built to carry it.
 */
function placementNote(required: RequiredTechnique[], phaseTimes: readonly number[]): string {
    const placed = required
        .map(({ skill, atPhase }) => ({
            skill,
            atPhase,
            at: atPhase === undefined ? undefined : phaseTimes[atPhase - 1],
        }))
        .filter(
            (one): one is { skill: string; atPhase: number; at: number } => one.at !== undefined,
        );
    if (placed.length === 0) {
        return '';
    }
    const list = placed
        .map(
            ({ skill, atPhase, at }) => `\`${skill}\` belongs to phase ${atPhase} (${seconds(at)})`,
        )
        .join(', and ');
    return (
        `${list}. Build it there rather than across the whole graphic: a technique spread over ` +
        `every element is what turns one idea into a frame full of them.`
    );
}

/** Only for a graphic that draws something — the method is dead weight on a frame of type. */
function drawingNote(
    required: RequiredTechnique[],
    drawsObject: boolean,
    methodLoaded: boolean,
): string {
    if (
        !drawsObject ||
        required.some(({ skill }) => skill === SKILL_NAMES.objectConstructionMethod)
    ) {
        return '';
    }
    const method = methodLoaded
        ? `construct the shape by the method in \`${SKILL_NAMES.objectConstructionMethod}\`, ` +
          `already loaded above`
        : `load \`${SKILL_NAMES.objectConstructionMethod}\` as well and construct the shape by ` +
          `its method`;
    return (
        `This graphic draws an object, and there is no icon library, symbol set or clip art here — ` +
        `${method}. It decides whether the thing reads as the thing at all, and reaching for ` +
        `freehand SVG without it is the most reliable way to produce a graphic that has to be ` +
        `thrown away.`
    );
}
